/* eslint-disable @typescript-eslint/no-floating-promises */
import platform from '@play-co/gcinstant';
import View from '@play-co/timestep-core/lib/ui/View';
import ScrollView from '@play-co/timestep-core/lib/ui/ScrollView';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import uiConfig from 'src/lib/ui/config';
import DropdownView from 'src/lib/ui/components/DropdownView';
import StateObserver from 'src/StateObserver';
import ButtonView from '@play-co/timestep-core/lib/ui/widget/ButtonView';
import Application from 'src/Application';
import {
  defaultDevSettings,
  DevSettingID,
  devSettings,
} from 'src/lib/settings';
import i18n from 'src/lib/i18n/i18n';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { dev_postAdminMessage } from '@play-co/replicant';
import { duration } from 'src/replicant/utils/duration';
import {
  Pet,
  PetAttribute,
  PetEvolutionStage,
  PetSpecies,
  PetStatusEffect,
} from 'src/replicant/buruburu/pet';
import { inviteAsync } from 'src/lib/sharing';
import { getCreativeText } from 'src/creatives/text';
import { getRandomShareInviteCreative } from 'src/creatives/share';
import { TutorialSteps } from 'src/replicant/buruburu/tutorial/tutorial.ruleset';
import { showTutorial } from 'src/game/tutorial/tutorial.util';
import { getRandomItemFromArray } from 'src/replicant/utils/random';
import { milestones } from 'src/replicant/buruburu/level/level.ruleset';
import { levelUpSequence } from 'src/game/level/level.sequence';
import { chatbotAssets } from 'src/replicant/chatbot/messageTemplates';
import { statusType } from 'src/replicant/chatbot/flex/status';
import { createEmitter } from 'src/lib/Emitter';
import { formatPSTTime } from 'src/replicant/utils/time';
import { Duo } from 'src/replicant/duo';
import { DuoItem } from 'src/replicant/buruburu/care/care.ruleset';
import { refresh } from 'src/lib/errors';
import { startDebutSequence } from 'src/game/duo/duo.sequence';
import { restartNativeApp } from 'src/lib/native/native.util';
import { assertNever } from 'src/replicant/utils';
import { guessIsNativeAppInstalled } from '../../native/native.util';
import { redirectToInstagram, shareLinkToInstagram } from 'src/lib/instagram';
import { redirectToMessenger, shareLinkToMessenger } from 'src/lib/messenger';
import { getLocalTimezone, formatLocalTime } from 'src/replicant/utils/time';
import { getClientTimeZone } from 'src/lib/utils';
import { encodeQuickReplyPayloadBase64 } from 'src/replicant/chatbot/chatbot.utils';
import { getConversationalImageId } from 'src/replicant/chatbot/chatbotConversation';
import { getLocaleConfig } from 'src/replicant/creatives/text';
import { getQuizEggChatData } from 'src/replicant/buruburu/petChatbot/petChatbot.scheduledActions';

type Setting = {
  cheat: (
    data: Setting,
    button: ButtonScaleViewWithText | ButtonScaleView | ButtonView,
    api?: PopupCheats,
  ) => void;

  text?: string;
  localeText?: () => string;

  amount?: number;
  attribute?: PetAttribute;
  statusEffect?: PetStatusEffect;
  species?: PetSpecies;
  stage?: PetEvolutionStage;
  nativePlatform?: 'apple' | 'google';
  isBlocked?: boolean;
  id?: string;
  color?: string;

  on?: boolean;
};

type Settings = { [key: string]: Setting[] };

type Item = View & { dropdown: DropdownView<Setting> };

const skin = { root: 'assets' };

export default class PopupCheats extends PopupBasic {
  private app: Application;
  comboOpened: boolean;
  settings: Settings;
  container: ScrollView;
  items: Item[];

  constructor(
    private creationOpts: {
      superview: View;
      app: Application;
      close: () => void;
    },
  ) {
    super({
      superview: creationOpts.superview,
      close: creationOpts.close,

      zIndex: 9999,

      width: 600,
      height: 960,
      tracking: { feature: 'cheats', $subFeature: 'popup-cheats' },
    });

    this.app = creationOpts.app;
    this.comboOpened = false;

    // define settings

    this.settings = {
      instagram: [
        {
          cheat: () =>
            shareLinkToInstagram({
              text: 'Hello world!',
              title: 'This is the OG title!',
              description: 'This is the OG description!',
              image: 'https://placekitten.com/1000',
              data: {
                feature: 'TEST',
                $subFeature: 'SUB_TEST',
              },
              chatbot: {
                messageText:
                  'Your friend used the cheats menu to create the link you just clicked on!',
                imageUrl: 'https://placekitten.com/600',
                buttonText: 'Join them!',
              },
              method: 'direct',
            }),
          text: 'IG Share',
          color: 'primary',
        },
        {
          cheat: () => redirectToInstagram(),
          text: 'IG Redirect',
          color: 'primary',
        },
      ],

      messenger: [
        {
          cheat: () =>
            shareLinkToMessenger({
              text: 'Hello world!',
              title: 'This is the OG title!',
              description: 'This is the OG description!',
              image: 'https://placekitten.com/1000',
              data: {
                feature: 'TEST',
                $subFeature: 'SUB_TEST',
              },
              chatbot: {
                messageText:
                  'Your friend used the cheats menu to create the link you just clicked on!',
                imageUrl: 'https://placekitten.com/600',
                buttonText: 'Join them!',
              },
              method: 'native',
            }),
          text: 'Messenger Share (native)',
          color: 'primary',
        },
        {
          cheat: () =>
            shareLinkToMessenger({
              text: 'Hello world!',
              title: 'This is the OG title!',
              description: 'This is the OG description!',
              image: 'https://placekitten.com/1000',
              data: {
                feature: 'TEST',
                $subFeature: 'SUB_TEST',
              },
              chatbot: {
                messageText:
                  'Your friend used the cheats menu to create the link you just clicked on!',
                imageUrl: 'https://placekitten.com/600',
                buttonText: 'Join them!',
              },
              method: 'direct',
            }),
          text: 'Messenger Share (direct)',
          color: 'primary',
        },
        {
          cheat: () => redirectToMessenger(),
          text: 'Messenger Redirect',
          color: 'primary',
        },
      ],

      conversation: [
        {
          cheat: async () => this.sendChatbotHungryGenericMessage(),
          text: 'Chatbot Hungry Generic',
          color: 'primary',
        },
        {
          cheat: async () => this.sendChatbotHungryActionedMessage(),
          text: 'Chatbot Hungry Actioned',
          color: 'primary',
        },
        {
          cheat: async () => this.sendChatbotSampleMessage(),
          text: 'Chatbot Sample Message',
          color: 'primary',
        },
        {
          cheat: async () => this.sendChatbotQuizEggChat(),
          text: 'Chatbot Quiz Egg Chat',
          color: 'primary',
        },
      ],

      time: [
        {
          cheat: () => {},
          text: 'Time',
          color: 'cancel',
        },
      ],

      cooldowns: [
        {
          cheat: () => {
            const cooldowns = StateObserver.getState().user?.cooldowns ?? {};
            for (let id in cooldowns) {
              StateObserver.invoke.setCooldown({ id, timestamp: 0 });
            }
            return true;
          },
          text: 'Reset Cooldowns',
          color: 'primary',
        },
      ],

      coins: [
        { cheat: this.addCoins, localeText: () => '100', amount: 100 },
        { cheat: this.addCoins, localeText: () => '1000', amount: 1000 },
        { cheat: this.addCoins, localeText: () => '10000', amount: 10000 },
        { cheat: this.addCoins, localeText: () => '-100', amount: -100 },
        { cheat: this.addCoins, localeText: () => '-1000', amount: -1000 },
        { cheat: this.addCoins, localeText: () => '-10000', amount: -10000 },
      ],

      gems: [
        { cheat: this.addGems, localeText: () => '1', amount: 1 },
        { cheat: this.addGems, localeText: () => '50', amount: 50 },
        { cheat: this.addGems, localeText: () => '100', amount: 100 },
        { cheat: this.addGems, localeText: () => '1000', amount: 1000 },
        { cheat: this.addGems, localeText: () => '10000', amount: 10000 },
        { cheat: this.addGems, localeText: () => '-100', amount: -100 },
        { cheat: this.addGems, localeText: () => '-1000', amount: -1000 },
        { cheat: this.addGems, localeText: () => '-10000', amount: -10000 },
      ],

      energy: [
        { cheat: this.addEnergy, localeText: () => '1', amount: 1 },
        { cheat: this.addEnergy, localeText: () => '10', amount: 10 },
        { cheat: this.addEnergy, localeText: () => '100', amount: 100 },
        { cheat: this.addEnergy, localeText: () => '-1', amount: -1 },
        { cheat: this.addEnergy, localeText: () => '-10', amount: -10 },
        { cheat: this.addEnergy, localeText: () => '-100', amount: -100 },
      ],

      stomach: [
        {
          cheat: this.setAttribute,
          localeText: () => '+10%',
          amount: 10,
          attribute: 'stomach',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '+1%',
          amount: 1,
          attribute: 'stomach',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '+100%',
          amount: 100,
          attribute: 'stomach',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '-1%',
          amount: -1,
          attribute: 'stomach',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '-10%',
          amount: -10,
          attribute: 'stomach',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '-100%',
          amount: -100,
          attribute: 'stomach',
        },
      ],
      love: [
        {
          cheat: this.setAttribute,
          localeText: () => '+10%',
          amount: 10,
          attribute: 'love',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '+1%',
          amount: 1,
          attribute: 'love',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '+100%',
          amount: 100,
          attribute: 'love',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '-1%',
          amount: -1,
          attribute: 'love',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '-10%',
          amount: -10,
          attribute: 'love',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '-100%',
          amount: -100,
          attribute: 'love',
        },
      ],
      dirtiness: [
        {
          cheat: this.setAttribute,
          localeText: () => '+10%',
          amount: 10,
          attribute: 'dirtiness',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '+1%',
          amount: 1,
          attribute: 'dirtiness',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '+100%',
          amount: 100,
          attribute: 'dirtiness',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '-1%',
          amount: -1,
          attribute: 'dirtiness',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '-10%',
          amount: -10,
          attribute: 'dirtiness',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '-100%',
          amount: -100,
          attribute: 'dirtiness',
        },
      ],
      toothAcidity: [
        {
          cheat: this.setAttribute,
          localeText: () => '+10%',
          amount: 10,
          attribute: 'toothAcidity',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '+1%',
          amount: 1,
          attribute: 'toothAcidity',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '+100%',
          amount: 100,
          attribute: 'toothAcidity',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '-1%',
          amount: -1,
          attribute: 'toothAcidity',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '-10%',
          amount: -10,
          attribute: 'toothAcidity',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '-100%',
          amount: -100,
          attribute: 'toothAcidity',
        },
      ],
      urine: [
        {
          cheat: this.setAttribute,
          localeText: () => '+10%',
          amount: 10,
          attribute: 'urine',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '+1%',
          amount: 1,
          attribute: 'urine',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '+100%',
          amount: 100,
          attribute: 'urine',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '-1%',
          amount: -1,
          attribute: 'urine',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '-10%',
          amount: -10,
          attribute: 'urine',
        },
        {
          cheat: this.setAttribute,
          localeText: () => '-100%',
          amount: -100,
          attribute: 'urine',
        },
      ],
      share: [
        {
          cheat: () => {
            this.sendShareInvite();
          },
          localeText: () => 'Invite',
        },
      ],

      chatbot: [
        {
          cheat: async () => this.sendChatbotFullEnergyMessage(),
          localeText: () => 'Full energy',
        },
        {
          cheat: async () => this.sendChatbotStatusMessage('dying'),
          localeText: () => 'Status dying',
        },
        {
          cheat: async () => this.sendChatbotStatusMessage('dead'),
          localeText: () => 'Status dead',
        },
        {
          cheat: async () => this.sendChatbotStatusMessage('dirty'),
          localeText: () => 'Status dirty',
        },
        {
          cheat: async () => this.sendChatbotStatusMessage('evolving'),
          localeText: () => 'Status evolve',
        },
        {
          cheat: async () => this.sendChatbotStatusMessage('hungry'),
          localeText: () => 'Status hungry',
        },
        {
          cheat: async () => this.sendChatbotStatusMessage('lonely'),
          localeText: () => 'Status lonely',
        },
        {
          cheat: async () => this.sendChatbotStatusMessage('scared'),
          localeText: () => 'Status scared',
        },
        {
          cheat: async () => this.sendChatbotStatusMessage('sick'),
          localeText: () => 'Status sick',
        },
        {
          cheat: async () => this.sendChatbotStatusMessage('tooth_decay'),
          localeText: () => 'Status tooth_decay',
        },
        {
          cheat: async () => this.sendChatbotStatusMessage('pee'),
          localeText: () => 'Status urinate',
        },
        {
          cheat: async () => this.sendChatbotStatusMessage('very_pee'),
          localeText: () => 'Status very urinate',
        },
        {
          cheat: async () => this.sendChatbotStatusMessage('very_dirty'),
          localeText: () => 'Status very dirty',
        },
        {
          cheat: async () => this.sendChatbotStatusMessage('very_hungry'),
          localeText: () => 'Status very hungry',
        },
        {
          cheat: async () => this.sendChatbotStatusMessage('very_lonely'),
          localeText: () => 'Status very lonely',
        },
      ],

      tutorial: TutorialSteps.map((step) => ({
        cheat: () =>
          showTutorial(step, {
            $stepIndex: -1,
            $stepName: 'cheating',
          }),
        localeText: () => step,
      })),

      receive: [
        {
          cheat: async () => {
            const userId = getRandomItemFromArray(
              [...platform.friendIds],
              Math.random,
            );

            StateObserver.invoke.cheats_receiveDuoResponse({
              userId,
              accepted: true,
            });
          },
          localeText: () => 'Duo response: accepted',
        },
        {
          cheat: async () => {
            const userId = getRandomItemFromArray(
              [...platform.friendIds],
              Math.random,
            );

            StateObserver.invoke.cheats_receiveDuoResponse({
              userId,
              accepted: false,
            });
          },
          localeText: () => 'Duo response: declined',
        },

        {
          cheat: async () => {
            const userId = getRandomItemFromArray(
              [...platform.friendIds],
              Math.random,
            );

            StateObserver.invoke.cheats_receiveDuoInvitation({ userId });
          },
          localeText: () => 'Duo invitation',
        },

        {
          cheat: async () => {
            const userId = getRandomItemFromArray(
              [...platform.friendIds],
              Math.random,
            );

            StateObserver.invoke.cheats_receive_gift({ userId });
          },
          localeText: () => 'Gift from friend',
        },
      ],

      reset: [
        {
          cheat: async () => {
            StateObserver.invoke.cheats_reset_gifts();
          },
          localeText: () => 'Gifts',
        },
        {
          cheat: async () => {
            StateObserver.invoke.cheats_reset_invites();
          },
          localeText: () => 'Invites',
        },
      ],

      addXP: milestones.map((milestone) => ({
        cheat: async () => {
          const { user } = StateObserver.getState();
          const { getCurrentEvolution } = Pet.getters(user);
          const { stage } = getCurrentEvolution();
          const { stage: nextStage } = Pet.getters(
            user,
          ).getNextSpeciesEvolution(Math.random) ?? { stage: 'adult' };

          await StateObserver.invoke.cheat_addXP({
            milestone,
            petStage: milestone === 'evolution' ? nextStage : stage,
          });

          // Show level up sequence if needed.
          await levelUpSequence();
        },
        localeText: () => milestone,
      })),

      scene: [
        {
          cheat: () =>
            startDebutSequence({
              id: 'foo',
              profile: {
                name: 'Foo',
                photo: 'https://i.imgur.com/1Q1Q1Q1.jpg',
                visits: 0,
                visited: 0,
                lastLogin: 0,
              },
              pet: {
                name: 'Bar',
                species: 'giraffe',
                age: 0,
              },
              invitation: {
                item: 'i_vowsCoffee',
                timestamp: 0,
                status: 'accepted',
                lastReminder: 0,
              },
            }),
          localeText: () => 'Debut',
        },
      ],

      effects: [
        {
          cheat: this.togglePetStatusEffect,
          text: 'Sick',
          statusEffect: 'sick',
          on: Pet.getters(StateObserver.getState().user).hasStatusEffect(
            'sick',
            StateObserver.now(),
          ),
        },
        {
          cheat: this.togglePetStatusEffect,
          text: 'Sleeping',
          statusEffect: 'asleep',
          on: Pet.getters(StateObserver.getState().user).hasStatusEffect(
            'asleep',
            StateObserver.now(),
          ),
        },
        {
          cheat: this.togglePetStatusEffect,
          text: 'Evolving',
          statusEffect: 'evolving',
          on: Pet.getters(StateObserver.getState().user).hasStatusEffect(
            'evolving',
            StateObserver.now(),
          ),
        },
        {
          cheat: this.togglePetStatusEffect,
          text: 'Scared',
          statusEffect: 'scared',
          on: Pet.getters(StateObserver.getState().user).hasStatusEffect(
            'scared',
            StateObserver.now(),
          ),
        },
        {
          cheat: this.togglePetStatusEffect,
          text: 'Tooth decay',
          statusEffect: 'toothDecay',
          on: Pet.getters(StateObserver.getState().user).hasStatusEffect(
            'toothDecay',
            StateObserver.now(),
          ),
        },
        {
          cheat: this.togglePetStatusEffect,
          text: 'Want to go out',
          statusEffect: 'wantToGoOut',
          on: Pet.getters(StateObserver.getState().user).hasStatusEffect(
            'wantToGoOut',
            StateObserver.now(),
          ),
        },
        {
          cheat: this.toggleHadVisitor,
          text: 'Had visitor',
          on: Pet.getters(StateObserver.getState().user).hasStatusEffect(
            'hadVisitor',
            StateObserver.now(),
          ),
        },
        {
          cheat: this.togglePetStatusEffect,
          text: 'Dying',
          statusEffect: 'dying',
          on: Pet.getters(StateObserver.getState().user).hasStatusEffect(
            'dying',
            StateObserver.now(),
          ),
        },
        {
          cheat: this.togglePetStatusEffect,
          text: 'Dead',
          statusEffect: 'dead',
          on: Pet.getters(StateObserver.getState().user).hasStatusEffect(
            'dead',
            StateObserver.now(),
          ),
        },
      ],
      species: [
        {
          cheat: this.setSpecies,
          localeText: () => 'Ball - Baby',
          species: 'ball',
          stage: 'baby',
        },

        {
          cheat: this.setSpecies,
          localeText: () => 'Starball - Baby',
          species: 'starball',
          stage: 'baby',
        },

        {
          cheat: this.setSpecies,
          localeText: () => 'Crab - Kid',
          species: 'crab',
          stage: 'kid',
        },

        {
          cheat: this.setSpecies,
          localeText: () => 'Hamster - Kid',
          species: 'hamster',
          stage: 'kid',
        },

        {
          cheat: this.setSpecies,
          localeText: () => 'Turtle - Kid',
          species: 'turtle',
          stage: 'kid',
        },

        {
          cheat: this.setSpecies,
          localeText: () => 'Dog - Youth',
          species: 'dog',
          stage: 'youth',
        },

        {
          cheat: this.setSpecies,
          localeText: () => 'Seaotter - Youth',
          species: 'seaotter',
          stage: 'youth',
        },

        {
          cheat: this.setSpecies,
          localeText: () => 'Cat - Youth',
          species: 'cat',
          stage: 'youth',
        },

        {
          cheat: this.setSpecies,
          localeText: () => 'Whale - Adult',
          species: 'whale',
          stage: 'adult',
        },

        {
          cheat: this.setSpecies,
          localeText: () => 'Giraffe - Adult',
          species: 'giraffe',
          stage: 'adult',
        },
      ],

      visit: [
        {
          cheat: () => this.setVisitKickedOut(),
          text: 'Set Visit Kicked Out',
          color: 'secondary',
        },
      ],

      native: [
        {
          cheat: this.toggleNativeAppInstalled,
          text: 'Apple',
          nativePlatform: 'apple',
          on: guessIsNativeAppInstalled('IOS'),
        },
        {
          cheat: this.toggleNativeAppInstalled,
          text: 'Google',
          nativePlatform: 'google',
          on: guessIsNativeAppInstalled('ANDROID'),
        },
      ],

      nativeResets: [
        {
          text: 'Restart App',
          color: 'primary',
          cheat: async () => restartNativeApp(false),
        },
        {
          text: 'Restart App (Force Login)',
          color: 'primary',
          cheat: async () => restartNativeApp(true),
        },
      ],

      resets: [
        {
          cheat: () => refresh(false),
          text: 'Reload Game',
          color: 'primary',
        },
        {
          cheat: this.resetGame.bind(this),
          text: 'Reset Game',
          color: 'cancel',
        },
      ],

      switchers: (Object.keys(defaultDevSettings) as DevSettingID[]).map(
        (setting) => ({
          cheat: this.toggleDevSetting(setting),
          text: setting,
          on: devSettings.get(setting),
        }),
      ),
    };

    if (process.env.REPLICANT_OFFLINE) {
      // Coins ---------------------------------------------------
      {
        const cheat = this.postAdminMessage('admin_addCoins');
        this.settings['+coins'] = [
          { cheat, localeText: () => '+5 Coins', amount: 5 },
          { cheat, localeText: () => '+50 Coins', amount: 50 },
          { cheat, localeText: () => '+500 Coins', amount: 500 },
        ];
      }

      {
        const cheat = this.postAdminMessage('admin_removeCoins');
        this.settings['-coins'] = [
          { cheat, localeText: () => '-5 Coins', amount: 5 },
          { cheat, localeText: () => '-50 Coins', amount: 50 },
          { cheat, localeText: () => '-500 Coins', amount: 500 },
        ];
      }

      {
        const cheat = this.postAdminMessage('admin_setCoins');
        this.settings['set_coins'] = [
          { cheat, localeText: () => '0 Coins', amount: 0 },
          { cheat, localeText: () => '100 Coins', amount: 100 },
        ];
      }

      // Gems ----------------------------------------------------
      {
        const cheat = this.postAdminMessage('admin_addFreeGems');
        this.settings['+gems'] = [
          { cheat, localeText: () => '+5 Gems', amount: 5 },
          { cheat, localeText: () => '+50 Gems', amount: 50 },
          { cheat, localeText: () => '+500 Gems', amount: 500 },
        ];
      }

      {
        const cheat = this.postAdminMessage('admin_removeGems');
        this.settings['-gems'] = [
          { cheat, localeText: () => '-5 Gems', amount: 5 },
          { cheat, localeText: () => '-50 Gems', amount: 50 },
          { cheat, localeText: () => '-500 Gems', amount: 500 },
        ];
      }

      {
        const cheat = this.postAdminMessage('admin_setFreeGems');
        this.settings['set_gems'] = [
          { cheat, localeText: () => '0 Gems', amount: 0 },
          { cheat, localeText: () => '100 gems', amount: 100 },
        ];
      }
    }

    this.settings['fate'] = [];

    this.container = new ScrollView({
      superview: this.box,
      x: 24,
      y: 60,
      width: this.box.style.width - 48,
      height: this.box.style.height - 95,
      scrollX: false,
      scrollY: true,
    });

    // create items

    this.items = [];
    Object.keys(this.settings).forEach((key, i) => {
      this.items.push(this.createItem(i, key));
    });

    const lastItem = this.items[this.items.length - 1];

    this.container.updateOpts({
      scrollBounds: {
        minY: 0,
        maxY: lastItem.style.y + lastItem.style.height,
      },
    });

    this.updateContainerBounds();
  }

  private createItem(i: number, key: string) {
    const lastItem = this.items[i - 1];

    const item = new View({
      backgroundColor: key !== 'switchers' ? 'rgba(0, 0, 0, 0.5)' : null,
      superview: this.container,
      x: 0,
      y: lastItem ? lastItem.style.y + lastItem.style.height + 10 : 0,
      width: this.container.style.width,
      height: 73,
    }) as Item;

    const title = new LangBitmapFontTextView({
      superview: item,
      x: 20,
      y: 0,
      width: 140,
      height: 73,
      align: 'left',
      verticalAlign: 'center',
      size: 20,
      color: 'white',
      wordWrap: false,
      font: bitmapFonts('Body'),
      localeText: () => (key === 'spins' ? '' : key.toUpperCase()),
    });

    switch (key) {
      case 'time':
        title.updateOpts({
          width: item.style.width - 40,
          height: 20,
          y: 8,
          align: 'center',
        });
        this.createTimeButtons(item, this.settings[key]);
        break;
      case 'visit':
      case 'nativeResets':
      case 'resets':
      case 'instagram':
      case 'messenger':
      case 'cooldowns':
      case 'conversation':
        this.createHorizontalButtons(item, this.settings[key]);
        break;
      case 'effects':
      case 'native':
      case 'switchers':
        this.createSwitchers(item, this.settings[key]);
        break;
      case 'fate':
        this.createFateSchedule(item);
        break;
      default:
        item.dropdown = this.createDropdown(item, this.settings[key]);
        this.createApplyButton(item);
        break;
    }

    return item;
  }

  private createDropdown(parent: View, arr: Setting[]) {
    const x = 170;
    const width = 245;

    const dropdown = new DropdownView({
      superview: parent,
      x,
      y: 7,
      width,
      height: 60,
      data: arr,
    });

    dropdown.onOpen = () => {
      // close all dropdown boxes except current
      this.items.map((item) => {
        if (item.dropdown && item.dropdown !== dropdown) {
          item.dropdown.close();
        }
      });
    };

    dropdown.onSelect = (data: any) => {
      // console.log('onSelectDropdown:', data);
    };

    return dropdown;
  }

  private createApplyButton(item: Item) {
    const button = new ButtonScaleViewWithText({
      ...uiConfig.buttons.primary,
      superview: item,
      localeText: () => 'Apply',
      fontSize: 20,
      font: bitmapFonts('Body'),
      x: this.box.style.width - 115,
      y: item.style.height * 0.5,
      width: 120,
      height: 62,
      centerOnOrigin: true,

      onClick: async () => {
        const data = item.dropdown.selectedData;
        data.cheat && data.cheat(data, button);
      },

      tracking: { feature: 'cheats', $subFeature: 'apply' },
    });
  }

  private createMultipleButtons(item: View, count: number, data: Setting[]) {
    const buttonData = data[0];

    const columns = Math.min(count, 10);

    const left = 14;
    const top = 34;
    const width = Math.floor(520 / columns);
    const height = 52;

    for (let i = 0; i < count; i++) {
      const x = left + width / 2 + width * (i % columns);
      const y = top + height / 2 + height * Math.floor(i / columns);

      const button = new ButtonScaleViewWithText({
        ...uiConfig.buttons.cancel,

        superview: item,
        localeText: () => (i + 1).toString(),
        fontSize: 18,
        font: bitmapFonts('Body'),
        x,
        y,
        width,
        height,
        centerOnOrigin: true,

        onClick: async () => {
          buttonData.amount = i;
          buttonData.cheat && buttonData.cheat(buttonData, button);
        },
        tracking: { feature: 'cheats', $subFeature: `cheat-${count}` },
      });
    }

    item.updateOpts({
      height: top + height * Math.ceil(count / columns) + 14,
    });
  }

  private createTimeButtons(item: View, data: Setting[]) {
    const offsetLabel = new LangBitmapFontTextView({
      superview: item,
      width: 100,

      x: item.style.width / 2 - 4,
      y: 48,
      align: 'center',
      verticalAlign: 'center',
      size: 22,
      color: 'white',
      wordWrap: false,
      font: bitmapFonts('Body'),
      localeText: () => '',
      centerOnOrigin: true,
    });

    const timeLabel = new LangBitmapFontTextView({
      superview: item,
      width: 500,
      x: 270,
      y: 102,
      align: 'center',
      verticalAlign: 'center',
      size: 26,
      color: 'white',
      wordWrap: false,
      font: bitmapFonts('Body'),
      localeText: () => '',
      centerOnOrigin: true,
    });

    const resetButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,

      superview: item,
      labelOffsetX: -2,
      localeText: () => 'Reset',
      fontSize: 18,
      font: bitmapFonts('Body'),
      x: 460,
      y: 240,
      width: 150,
      height: 60,
      centerOnOrigin: true,

      onClick: async () => {
        setOffset(0);
        setTimezone(getClientTimeZone());
      },

      tracking: { feature: 'cheats', $subFeature: 'reset-time' },
    });

    const createTimeButton = (offset: number, text: string, amount: number) => {
      const button = new ButtonScaleViewWithText({
        ...uiConfig.buttons.cancel,

        superview: item,
        labelOffsetX: -2,
        localeText: () => text,
        fontSize: 18,
        font: bitmapFonts('Body'),
        x: offset * 52 + 40,
        y: 60,
        width: 52,
        height: 52,
        centerOnOrigin: true,

        onClick: () => setOffset(amount),

        tracking: { feature: 'cheats', $subFeature: `time-${text}` },
      });
    };

    const createTimeZoneButton = (offset: number, timeZone: string) => {
      const width = 130;
      new ButtonScaleViewWithText({
        ...uiConfig.buttons.cancel,

        superview: item,
        labelOffsetX: -2,
        localeText: () => timeZone,
        fontSize: 18,
        font: bitmapFonts('Body'),
        x: offset * width + width / 2 + 14,
        y: 170,
        width,
        height: 52,
        centerOnOrigin: true,
        onClick: () => setTimezone(timeZone),
        tracking: { feature: 'cheats', $subFeature: `timeZone-${timeZone}` },
      });
    };

    let keepPetHealthy = true;
    const healthSwitcherTitle = new LangBitmapFontTextView({
      superview: item,
      x: 120,
      y: 230,
      align: 'left',
      verticalAlign: 'center',
      size: 18,
      color: 'white',
      wordWrap: false,
      font: bitmapFonts('Body'),
      localeText: () => 'Keep Pet Healthy',
      centerOnOrigin: true,
    });

    const healthSwitcher = new ButtonScaleView({
      superview: item,
      x: 20,
      y: 220,
      width: 89,
      height: 42,
      onClick: async () => {
        keepPetHealthy = !keepPetHealthy;
        updateHealthSwitcher();
      },
      tracking: { feature: 'cheats', $subFeature: 'switcher' },
    });

    const updateHealthSwitcher = () => {
      this.updateSwitcher(healthSwitcher, keepPetHealthy);
      const state = keepPetHealthy ? 'ON' : 'OFF';
      healthSwitcherTitle.updateOpts({
        localeText: () => 'Keep Pet Healthy ' + state,
      });
    };

    updateHealthSwitcher();

    const setTimezone = async (timeZone) => {
      await StateObserver.invoke.updateUserClientSideData({ timeZone });
      if (keepPetHealthy) this.fullPetRecover();
      updateLabels();
    };

    const setOffset = async (amount) => {
      let offset = 0;
      if (amount !== 0) {
        offset = amount + (await StateObserver.invoke.cheat_getClockOffset());
      }
      await StateObserver.invoke.cheat_setClockOffset({ offset });
      if (keepPetHealthy) this.fullPetRecover();
      updateLabels();
    };

    const updateLabels = async () => {
      const offset = await StateObserver.invoke.cheat_getClockOffset();
      const timeZone = getLocalTimezone(StateObserver.getState().user);

      timeLabel.localeText = () =>
        new Date(StateObserver.now()).toLocaleString('en-US', {
          timeZone: timeZone,
          dateStyle: 'medium',
          timeStyle: 'long',
        }) +
        ' ' +
        timeZone;

      const hrs = Math.floor(Math.abs(offset) / duration({ hours: 1 }));
      const hrsText = `${hrs}`.padStart(2, '0');

      const mins = Math.floor(
        (Math.abs(offset) % duration({ hours: 1 })) / duration({ minutes: 1 }),
      );
      const minsText = `${mins}`.padStart(2, '0');

      let offsetText = '0';
      if (offset !== 0) {
        offsetText = offset < 0 ? '-' : '+';
        offsetText += hrs < 100 ? hrsText + ':' + minsText : hrsText;
      }

      offsetLabel.localeText = () => offsetText;
    };

    updateLabels();

    createTimeButton(0, '-M', -duration({ days: 28 }));
    createTimeButton(1, '-D', -duration({ days: 1 }));
    createTimeButton(2, '-H', -duration({ hours: 1 }));
    createTimeButton(3, '-m', -duration({ minutes: 1 }));

    createTimeButton(6, '+m', duration({ minutes: 1 }));
    createTimeButton(7, '+H', duration({ hours: 1 }));
    createTimeButton(8, '+D', duration({ days: 1 }));
    createTimeButton(9, '+M', duration({ days: 28 }));

    createTimeZoneButton(0, 'America/Los_Angeles');
    createTimeZoneButton(1, 'America/Chicago');
    createTimeZoneButton(2, 'America/New_York');
    createTimeZoneButton(3, 'Europe/London');

    item.updateOpts({ height: 280 });
  }

  private createHorizontalButtons(item: View, data: Setting[]) {
    const remain = (data.length - 1) % 2 ? 1 : 2;
    let w = (item.style.width - 5) / data.length - 5 * (data.length - remain);
    if (data.length === 1) w -= 10;

    data.map((buttonData, index) => {
      const btnConfig = uiConfig.buttons[buttonData.color];
      const button = new ButtonScaleViewWithText({
        ...btnConfig,

        superview: item,
        localeText: () => buttonData.text,
        fontSize: 20,
        font: bitmapFonts('Body'),
        x: 5 + w / 2 + index * (w + 5),
        y: item.style.height * 0.5,
        width: w,
        height: 60,
        centerOnOrigin: true,

        onClick: () => {
          buttonData.cheat && buttonData.cheat(buttonData, button);
        },
      });
    });
  }

  private createFateSchedule(item: View) {
    item.updateOpts({ height: 200 });

    const view = new View({
      superview: item,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      x: 0,
      y: 0,
      width: item.style.width,
      height: 200,
    });

    const fateSeedView = new LangBitmapFontTextView({
      superview: item,
      x: 390,
      y: 10,
      width: 150,
      height: 25,
      wordWrap: true,
      align: 'right',
      verticalAlign: 'top',
      size: 18,
      color: 'white',
      font: bitmapFonts('Body'),
    });

    const textView = new LangBitmapFontTextView({
      superview: item,
      x: 20,
      y: 50,
      width: item.style.width,
      height: item.style.height,
      wordWrap: true,
      align: 'left',
      verticalAlign: 'top',
      size: 18,
      color: 'white',
      font: bitmapFonts('Body'),
    });

    const timeZone = getLocalTimezone(StateObserver.getState().user);
    createEmitter(view, ({ user }) => user.fate).addListener((fate) => {
      const sortedFate = Object.keys(fate.schedule)
        .map((id) => ({ state: id, timestamp: fate.schedule[id].timestamp }))
        .sort((a, b) => a.timestamp - b.timestamp)
        .map((item) => ({
          state: item.state,
          date: formatLocalTime(item.timestamp, timeZone),
        }))
        .map((item) => `${item.date} - ${item.state.toUpperCase()}`);

      const text = sortedFate.join('\n');
      textView.localeText = () => text;

      const height = 100 + Object.keys(fate.schedule).length * 28;

      item.updateOpts({ height: height });
      view.updateOpts({ height: height });
      textView.updateOpts({ height: height });

      fateSeedView.localeText = () => `${fate.seed}`;

      this.container.updateOpts({
        scrollBounds: {
          minY: 0,
          maxY: item.style.y + height,
        },
      });
    });
  }

  private createSwitchers(item: View, data: Setting[]) {
    item.updateOpts({
      height: item.style.height + 79 + (data.length - 1) * (73 + 10),
    });

    data.map((buttonData, index) => {
      const toggleContainer = new View({
        superview: item,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        x: 5,
        y: 79 + index * (73 + 10),
        width: item.style.width - 5,
        height: 73,
      });

      const title = new LangBitmapFontTextView({
        superview: toggleContainer,
        x: 120,
        y: 25,
        align: 'left',
        verticalAlign: 'center',
        size: 24,
        color: 'white',
        wordWrap: false,
        font: bitmapFonts('Body'),
        localeText: () => buttonData.text,
        centerOnOrigin: true,
      });

      const switcher = new ButtonScaleView({
        superview: toggleContainer,
        x: 10,
        y: 15,
        width: 89,
        height: 42,
        onClick: async () => {
          buttonData.cheat && buttonData.cheat(buttonData, switcher, this);
        },
        tracking: { feature: 'cheats', $subFeature: 'switcher' },
      });

      this.updateSwitcher(switcher, buttonData.on);
    });
  }

  public updateSwitcher(switcher: ButtonScaleView, on: boolean) {
    const url = on
      ? `${skin.root}/ui/shared/lossless/btn_toggle_on.png`
      : `${skin.root}/ui/shared/lossless/btn_toggle_off.png`;

    switcher.updateButtonImage(url);
  }

  // ===================================================

  private addCoins(data: Setting) {
    const amount = data.amount;
    console.log('Adding', amount, 'coins');
    StateObserver.invoke.cheat_addCoins({ amount });
  }

  private addGems(data: Setting) {
    const amount = data.amount;
    console.log('Adding', amount, 'gems');
    StateObserver.invoke.cheat_addGems({ amount });
  }

  private addEnergy(data: Setting) {
    const amount = data.amount;
    console.log('Adding', amount, 'energy');
    StateObserver.invoke.cheat_addEnergy({ amount });
  }

  private setAttribute(data: Setting) {
    const amount = data.amount;
    const attribute = data.attribute;
    StateObserver.invoke.cheat_setAttribute({ amount, attribute });
  }

  private fullPetRecover() {
    function turnOffPetEffect(effect: PetStatusEffect) {
      const now = StateObserver.now();
      const state = StateObserver.getState().user;
      const currentValue = Pet.getters(state).hasStatusEffect(effect, now);
      if (currentValue) {
        StateObserver.invoke.cheat_setEffect({ effect });
      }
    }

    function setPetAttribute(attribute: PetAttribute, value: number) {
      const now = StateObserver.now();
      const state = StateObserver.getState().user;
      const currentValue = Pet.getters(state).getAttributeValue(attribute, now);
      const amount = value - currentValue;
      StateObserver.invoke.cheat_setAttribute({ attribute, amount });
    }

    setPetAttribute('stomach', 75);
    setPetAttribute('love', 75);
    setPetAttribute('toothAcidity', 0);
    setPetAttribute('dirtiness', 0);
    setPetAttribute('urine', 0);
    turnOffPetEffect('sick');
    turnOffPetEffect('dead');
    turnOffPetEffect('evolving');
    turnOffPetEffect('toothDecay');
    turnOffPetEffect('dying');
    turnOffPetEffect('scared');
  }

  private togglePetStatusEffect(
    data: Setting,
    switcher: ButtonScaleViewWithText | ButtonScaleView | ButtonView,
    api: PopupCheats,
  ) {
    const effect = data.statusEffect;

    // Update the setting
    StateObserver.invoke.cheat_setEffect({ effect });

    // Update the UI
    api.updateSwitcher(
      switcher as ButtonScaleViewWithText,
      Pet.getters(StateObserver.getState().user).hasStatusEffect(
        effect,
        StateObserver.now(),
      ),
    );
  }

  private toggleHadVisitor(
    _,
    switcher: ButtonScaleViewWithText | ButtonScaleView | ButtonView,
    api: PopupCheats,
  ) {
    // Update the setting
    StateObserver.invoke.cheat_setHadVisitor();

    // Update the UI
    api.updateSwitcher(
      switcher as ButtonScaleViewWithText,
      Pet.getters(StateObserver.getState().user).hasStatusEffect(
        'hadVisitor',
        StateObserver.now(),
      ),
    );
  }

  private setSpecies(data: Setting) {
    const stage = data.stage;
    const species = data.species;

    StateObserver.invoke.cheat_setSpecies({ stage, species });
  }

  private async setVisitKickedOut() {
    await StateObserver.invoke.cheat_setVisitKickedOut();
    this.opts.close();
  }

  private async resetGame(data: Setting) {
    console.log('Reset Game');

    StateObserver.detachListeners();
    await StateObserver.invoke.cheat_reset();

    // Make sure to give time for the reset request to be sent to the server,
    // before triggering the page refresh
    await StateObserver.refreshMessages();

    platform.quit();

    if (window.confirm('Do you want to reload game?')) {
      window.location.reload();
    }
  }

  private async toggleNativeAppInstalled(
    data: Setting,
    switcher: ButtonScaleViewWithText | ButtonScaleView | ButtonView,
    api: PopupCheats,
  ) {
    let isInstalled: boolean;

    const platform = data.nativePlatform;
    const { native } = StateObserver.getState().user;
    switch (platform) {
      case 'apple':
        isInstalled = !native.appleLastLaunched;
        break;
      case 'google':
        isInstalled = !native.googleLastLaunched;
        break;
      default:
        assertNever(platform);
    }

    await StateObserver.invoke.cheat_setNativeAppInstalled({
      platform,
      isInstalled,
    });

    // Update the UI
    api.updateSwitcher(switcher as ButtonScaleViewWithText, isInstalled);
  }

  private toggleDevSetting(setting: DevSettingID) {
    return (
      _: Setting,
      switcher: ButtonScaleViewWithText | ButtonScaleView | ButtonView,
    ) => {
      // Update the setting
      const currentSettings = devSettings.load();
      currentSettings[setting] = !currentSettings[setting];
      devSettings.save(currentSettings);

      // Update the UI
      this.updateSwitcher(
        switcher as ButtonScaleViewWithText,
        currentSettings[setting],
      );
    };
  }

  private postAdminMessage(name: string) {
    return async (data: Setting) => {
      if (process.env.REPLICANT_OFFLINE) {
        await dev_postAdminMessage(StateObserver.replicant, platform.playerID, {
          name,
          args: { amount: data.amount, reason: 'cheat' },
        });

        await StateObserver.refreshMessages();
      }
    };
  }

  private async sendShareInvite() {
    await inviteAsync({
      debug: true,
      creativeAsset: await getRandomShareInviteCreative(),
      creativeText: getCreativeText('invite'),
      confirmPopup: true,
      data: {
        feature: 'TEST',
        $subFeature: 'SUB_TEST',
      },
    });
  }

  // ===================================================

  init(opts: {}) {
    super.init({});
    // update popup text
    this.title.setText(() => i18n('menu.items.cheats').toUpperCase());
  }

  //

  private updateContainerBounds() {
    const last = this.items[this.items.length - 1];

    const bottom = last.style.y + last.style.height;
    const dropdownHeight = last.dropdown
      ? last.dropdown.itemHeight * (last.dropdown.items.length - 1)
      : 0;

    this.container.updateOpts({
      scrollBounds: {
        minY: 0,
        maxY: bottom + dropdownHeight,
      },
    });
  }

  /**
   * Sending full energy OA(chatbot) message with test payload
   * Also, print finale template on console where it could be copied and paste to flex message simulator:
   * https://developers.line.biz/flex-simulator
   */
  private async sendChatbotFullEnergyMessage() {
    const { user } = StateObserver.getState();
    const { species } = Pet.getters(user).getCurrentEvolution();
    const locale = StateObserver.getLocale();
    const local = process.env.REPLICANT_ENDPOINT?.includes('localhost');
    const imageId = `static/${species}/fullenergy`;
    await StateObserver.invoke.cheat_send_chatbot_static_message({
      ...(local && { forceUserId: '6439105372806440' }),
      ...(local && { forcePlatform: 'instagram' }),
      template: 'messengerPlayButton',
      title: getLocaleConfig(locale).energy.full.text,
      imageUrl: imageId,
      payload: {
        $channel: 'UPDATE',
        feature: 'full-enery-cheat',
        $subFeature: 'full-enery',
        $creativeAssetID: imageId,
      },
    });
  }

  /**
   * Sending status OA(chatbot) message with test payload
   * Also, print finale template on console where it could be copied and paste to flex message simulator:
   * https://developers.line.biz/flex-simulator
   */
  private async sendChatbotStatusMessage(status: statusType) {
    const { user } = StateObserver.getState();
    const { species } = Pet.getters(user).getCurrentEvolution();
    const local = process.env.REPLICANT_ENDPOINT?.includes('localhost');
    const imageId = `static/${species}/${status}`;
    await StateObserver.invoke.cheat_send_chatbot_static_message({
      ...(local && { forceUserId: '6439105372806440' }),
      ...(local && { forcePlatform: 'instagram' }),
      template: 'messengerPlayButton',
      title: getLocaleConfig('en').status[status].text,
      imageUrl: imageId,
      payload: {
        $channel: 'UPDATE',
        feature: 'status-cheat',
        $subFeature: status,
        $creativeAssetID: imageId,
      },
    });
  }

  private async sendChatbotHungryGenericMessage() {
    const { user } = StateObserver.getState();
    const { species } = Pet.getters(user).getCurrentEvolution();
    const imageId = getConversationalImageId(species, 'feed');
    await StateObserver.invoke.cheat_send_chatbot_static_message({
      template: 'messengerQuickReplyButtons',
      title: 'Will you feed me?',
      imageUrl: imageId,
      payload: {
        $channel: 'UPDATE',
        feature: 'status-cheat',
        $subFeature: 'conversation-hungry-generic',
        $creativeAssetID: imageId,
      },
      quickReplyButtons: [
        {
          title: "I'll feed you!",
          type: 'postback',
          payload: encodeQuickReplyPayloadBase64({
            messageText: 'Yay! come on over!',
            buttonText: 'Play Trembly!',
            imageUrl: getConversationalImageId(species, 'yes'),
            species,
          }),
        },
        {
          title: 'No food for you!',
          type: 'postback',
          payload: encodeQuickReplyPayloadBase64({
            messageText:
              "You're being mean! I'll forgive you if you come play!",
            buttonText: 'Play Trembly!',
            imageUrl: getConversationalImageId(species, 'no'),
            species,
          }),
        },
      ],
    });
  }

  private async sendChatbotHungryActionedMessage() {
    const { user } = StateObserver.getState();
    const { species } = Pet.getters(user).getCurrentEvolution();
    const imageId = getConversationalImageId(species, 'feed');
    await StateObserver.invoke.cheat_send_chatbot_static_message({
      template: 'messengerQuickReplyButtons',
      title: 'Will you feed me?',
      imageUrl: imageId,
      payload: {
        $channel: 'UPDATE',
        feature: 'status-cheat',
        $subFeature: 'conversation-hungry-actioned',
        $creativeAssetID: imageId,
      },
      quickReplyButtons: [
        {
          title: 'Vibrating Food [40]',
          type: 'postback',
          payload: encodeQuickReplyPayloadBase64({
            messageText: "Thanks for the meal!\nNow let's play!",
            buttonText: 'Play Trembly!',
            action: 'pay-food-40',
            imageUrl: getConversationalImageId(species, 'thanks'),
            species,
          }),
        },
        {
          title: 'Gentle Sushi [60]',
          type: 'postback',
          payload: encodeQuickReplyPayloadBase64({
            messageText: "Thanks for the meal!\nNow let's play!",
            buttonText: 'Play Trembly!',
            action: 'pay-food-60',
            imageUrl: getConversationalImageId(species, 'thanks'),
            species,
          }),
        },
        {
          title: 'No food for you!',
          type: 'postback',
          payload: encodeQuickReplyPayloadBase64({
            messageText:
              "You're being mean! I'll forgive you if you come play!",
            buttonText: 'Play Trembly!',
            imageUrl: getConversationalImageId(species, 'no'),
            species,
          }),
        },
      ],
    });
  }

  private async sendChatbotSampleMessage() {
    await StateObserver.invoke.cheat_send_chatbot_static_message({
      template: 'messengerPlayButton',
      title: 'Play Trembly!',
      imageUrl: 'static/generic',
      payload: {
        $channel: 'UPDATE',
        feature: 'play-button',
        $subFeature: 'cheat',
        $creativeAssetID: 'static/generic',
      },
    });
  }

  private async sendChatbotQuizEggChat() {
    const quiz = getQuizEggChatData();
    const imageId = 'static/generic';
    const answerTitle = quiz.answers[0] + ' ' + quiz.answers[1];
    const answerSubtitle = quiz.answers[2];
    const quickReplyButtons = quiz.options.map((option) => {
      return {
        title: option,
        type: 'postback',
        payload: encodeQuickReplyPayloadBase64({
          messageText: answerTitle,
          messageSubtitle: answerSubtitle,
          buttonText: 'Play Trembly!',
        }),
      };
    });

    await StateObserver.invoke.cheat_send_chatbot_static_message({
      template: 'messengerQuickReplyButtons',
      title: quiz.title,
      imageUrl: imageId,
      payload: {
        $channel: 'UPDATE',
        feature: 'quiz-egg-chat-cheat',
        $subFeature: quiz.id,
        $creativeAssetID: imageId,
      },
      quickReplyButtons,
    });
  }
}
